import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import "../../styles/pagination-new.scss";

import defaultLefticon from "../../assets/dataTable/arrow-left.png";
import defaultRighticon from "../../assets/dataTable/arrow-right.png";
import activeLefticon from "../../assets/dataTable/arrow-left-active.png";
import activeRighticon from "../../assets/dataTable/arrow-right-active.png";

import GoToFirstArrowIcon from "../../assets/dataTable/pagination-arrow-left.png";
import GoToFirstArrowActiveIcon from "../../assets/dataTable/pagination-arrow-left-active.png";
import GoToLastArrowIcon from "../../assets/dataTable/pagination-arrow-right.png";
import GoToLastArrowActiveIcon from "../../assets/dataTable/pagination-arrow-right-active.png";
import { ReactComponent as ArrowRightThinWhiteSvg } from "../../assets/icons/arrow-right-thin-white.svg";
import { ReactComponent as ArrowRightThinBlackSvg } from "../../assets/icons/arrow-right-thin.svg";

/**
 * **Spec ->** https://www.notion.so/pttnewpos/SecondaryPagination-25df3dc51c0a445f83fedfa2860e5886
 */
const SecondaryPagination = ({
  pages,
  limits,
  onPageChange,
  defaultPage,
  onLimitChange,
  currentPage: current,
  defaultLimit,
  totalDocuments,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState("");
  const [numOfPage, setNumOfPage] = useState(null);
  const [customPage, setCustomPage] = useState({
    id: null,
    status: false,
  });

  useEffect(() => {
    if (defaultPage) {
      setCurrentPage(defaultPage);
    }

    if (current) {
      setCurrentPage(current);
    }

    if (defaultLimit) {
      setCurrentLimit(defaultLimit);
    }

    if (!defaultLimit) {
      setCurrentLimit(limits[0]);
    }

    if (totalDocuments < 0) {
      throw new Error("totalDocuments cannot be a negative number");
    }

    setNumOfPage(pages);
  }, [defaultPage, current, pages, defaultLimit]);

  const numOfPageArr = useMemo(() => {
    let arr = [];
    for (let count = 0; count < numOfPage; count++) {
      arr.push(count + 1);
    }
    return arr;
  }, [numOfPage]);

  const startPage = 1;
  const lastPage = numOfPageArr[numOfPageArr.length - 1];
  const isDefault = numOfPage <= 5;

  let displayFrom = (currentPage - 1) * currentLimit + 1;
  let displayTo = currentLimit * currentPage;
  const lastPageCalculated = Math.ceil(totalDocuments / currentLimit);
  if (currentPage === lastPageCalculated) {
    displayTo = totalDocuments;
  } else if (currentPage > lastPageCalculated) {
    displayFrom = "";
    displayTo = "";
  }

  const displayRange = `${displayFrom}-${displayTo} of ${totalDocuments}`;

  const isActiveImgLeft =
    currentPage !== startPage ? activeLefticon : defaultLefticon;
  const isActiveImgRight =
    currentPage !== lastPage ? activeRighticon : defaultRighticon;
  const isActiveImgGoToStart =
    currentPage !== startPage ? GoToFirstArrowActiveIcon : GoToFirstArrowIcon;
  const isActiveImgGoToLast =
    currentPage !== lastPage ? GoToLastArrowActiveIcon : GoToLastArrowIcon;

  // all function
  const handleOnFocusOut = (e) => {
    const { value } = e.target;

    setCustomPage({ id: null, status: false });

    if (Number(value) <= numOfPage && Number(value) >= startPage) {
      onPageChange(Number(value));
      setCurrentPage(Number(value));
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleOnFocusOut(event);
    } else if (!/\d/.test(event.key) && event.key.length === 1) {
      event.preventDefault();
    }
  };

  const handleOnClickPage = (e) => {
    const { id } = e.target;
    onPageChange(Number(id));
    setCurrentPage(Number(id));
  };

  const handleOnSetCustom = (e) => {
    const { id } = e.target;

    setCustomPage({ id: id, status: true });
  };

  const handleOnIncrease = () => {
    if (currentPage < numOfPage) {
      setCurrentPage((prev) => prev + 1);
      onPageChange(currentPage + 1);
    }
  };

  const handleOnReduce = () => {
    if (currentPage > startPage) {
      setCurrentPage((prev) => prev - 1);
      onPageChange(currentPage - 1);
    }
  };

  const handleOnSelected = (e) => {
    const { value } = e.target;
    onLimitChange(Number(value));
    setCurrentLimit(Number(value));
  };

  const handleOnGoStart = () => {
    setCurrentPage(1);
    onPageChange(1);
  };

  const handleOnGoLast = () => {
    setCurrentPage(pages);
    onPageChange(pages);
  };

  // all render type 1. default type 2. start type 3. middle type 4.end type
  const renderDefaultType = () => {
    return (
      <div className="c-pagination-new__pagination-group">
        {numOfPageArr.map(
          (item, index) =>
            index < numOfPage && (
              <button
                key={index}
                className="c-pagination-new__pagination-button"
                id={item}
                onClick={handleOnClickPage}
              >
                <span
                  id={item}
                  className={`${
                    currentPage === index + 1
                      ? "c-pagination-new__button-name--focus"
                      : "c-pagination-new__button-name"
                  }`}
                >
                  {item}
                </span>
              </button>
            )
        )}
      </div>
    );
  };

  const renderStartType = () => {
    return (
      <div className="c-pagination-new__pagination-group">
        {numOfPageArr.map(
          (item, index) =>
            index < 3 && (
              <button
                key={index}
                className="c-pagination-new__pagination-button"
                id={item}
                onClick={handleOnClickPage}
              >
                <span
                  id={item}
                  className={`${
                    currentPage === index + 1
                      ? "c-pagination-new__button-name--focus"
                      : "c-pagination-new__button-name"
                  }`}
                >
                  {item}
                </span>
              </button>
            )
        )}

        {customPage.status && customPage.id === "t1" ? (
          <input
            onBlur={handleOnFocusOut}
            onKeyDown={handleKeyDown}
            className="c-pagination-new__input"
            data-testid="testinput"
            autoFocus
          />
        ) : (
          <button
            className="c-pagination-new__pagination-button"
            id="t1"
            onClick={handleOnSetCustom}
          >
            ...
          </button>
        )}

        <button
          className="c-pagination-new__pagination-button"
          id={lastPage}
          onClick={handleOnClickPage}
        >
          <span
            id={lastPage}
            className={`${
              currentPage === lastPage + 1
                ? "c-pagination-new__button-name--focus"
                : "c-pagination-new__button-name"
            }`}
          >
            {lastPage}
          </span>
        </button>
      </div>
    );
  };

  const renderMiddleType = () => {
    return (
      <div className="c-pagination-new__pagination-group">
        <button
          className="c-pagination-new__pagination-button"
          id={startPage}
          onClick={handleOnClickPage}
        >
          <span id={startPage} className="c-pagination-new__button-name">
            {startPage}
          </span>
        </button>
        {customPage.status && customPage.id === "t2" ? (
          <input
            onBlur={handleOnFocusOut}
            onKeyDown={handleKeyDown}
            className="c-pagination-new__input"
            data-testid="testinput"
            autoFocus
          />
        ) : (
          <button
            className="c-pagination-new__pagination-button"
            id="t2"
            onClick={handleOnSetCustom}
          >
            ...
          </button>
        )}
        <button
          className="c-pagination-new__pagination-button"
          id={currentPage}
        >
          <span
            className="c-pagination-new__button-name"
            style={{ color: "#285FC9" }}
          >
            {currentPage}
          </span>
        </button>
        {customPage.status && customPage.id == "t3" ? (
          <input
            onBlur={handleOnFocusOut}
            onKeyDown={handleKeyDown}
            className="c-pagination-new__input"
            data-testid="testinput"
            autoFocus
          />
        ) : (
          <button
            className="c-pagination-new__pagination-button"
            id="t3"
            onClick={handleOnSetCustom}
          >
            ...
          </button>
        )}
        <button
          className="c-pagination-new__pagination-button"
          id={lastPage}
          onClick={handleOnClickPage}
        >
          <span id={lastPage} className="c-pagination-new__button-name">
            {lastPage}
          </span>
        </button>
      </div>
    );
  };

  const renderEndType = () => {
    return (
      <div className="c-pagination-new__pagination-group">
        <button
          className="c-pagination-new__pagination-button"
          id={startPage}
          onClick={handleOnClickPage}
        >
          <span
            id={startPage}
            className={`${
              currentPage === startPage
                ? "c-pagination-new__button-name--focus"
                : "c-pagination-new__button-name"
            }`}
          >
            {startPage}
          </span>
        </button>

        {customPage.status && customPage.id === "t4" ? (
          <input
            onBlur={handleOnFocusOut}
            onKeyDown={handleKeyDown}
            className="c-pagination-new__input"
            data-testid="testinput"
            autoFocus
          />
        ) : (
          <button
            className="c-pagination-new__pagination-button"
            id="t4"
            onClick={handleOnSetCustom}
          >
            ...
          </button>
        )}

        {numOfPageArr.slice(numOfPage - 3).map((item, index) => (
          <button
            key={index}
            className="c-pagination-new__pagination-button"
            id={item}
            onClick={handleOnClickPage}
          >
            <span
              id={item}
              className={`${
                currentPage === item
                  ? "c-pagination-new__button-name--focus"
                  : "c-pagination-new__button-name"
              }`}
            >
              {item}
            </span>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="c-pagination-new">
      <div className="c-pagination-new__select-group">
        <label className="c-pagination-new__label">Show</label>
        <select
          data-testid="test-select"
          className="c-pagination-new__selection"
          onChange={handleOnSelected}
          value={currentLimit}
        >
          {limits.map((item, index) => (
            <option
              className="c-pagination-new__option"
              key={index}
              value={item}
            >
              {item}
            </option>
          ))}
        </select>
        {totalDocuments !== 0 && (
          <label className="c-pagination-new__range">
            {displayRange}
          </label>
        )}
      </div>

      {pages > 1 && (
        <div className="c-pagination-new__pagination">
          {/* <button
            className="c-pagination-new__button"
            onClick={handleOnGoStart}
            disabled={currentPage === startPage}
          >
            <img src={isActiveImgGoToStart} height="14px" width="9px" />
          </button> */}
          <button
            className="c-pagination-new__button"
            onClick={handleOnReduce}
            disabled={currentPage === startPage}
          >
           <ArrowRightThinWhiteSvg  className="c-pagination-new__button--left"/>
          </button>

          {isDefault && renderDefaultType()}
          {!isDefault && currentPage < 4 && renderStartType()}
          {!isDefault &&
            currentPage >= 4 &&
            numOfPage - currentPage >= 3 &&
            renderMiddleType()}
          {!isDefault && numOfPage - currentPage < 3 && renderEndType()}

          <button
            className="c-pagination-new__button"
            onClick={handleOnIncrease}
            disabled={currentPage === lastPage}
          >
           <ArrowRightThinWhiteSvg />
          </button>
          {/* <button
            className="c-pagination-new__button"
            onClick={handleOnGoLast}
            disabled={currentPage === lastPage}
          >
            <img src={isActiveImgGoToLast} height="14px" width="9px" />
          </button> */}
        </div>
      )}
    </div>
  );
};

SecondaryPagination.propTypes = {
  /**
   * Pages to display
   */
  pages: PropTypes.number,

  /**
   * Available limits
   */
  limits: PropTypes.array,

  /**
   * Triggered when end-user clicks on a new page
   */
  onPageChange: PropTypes.func,

  /**
   * Triggered when end-user selects a new limit
   */
  onLimitChange: PropTypes.func,

  /**
   * Default page to start with
   */
  defaultPage: PropTypes.number,
  currentPage: PropTypes.number,
  defaultLimit: PropTypes.number,
  totalDocuments: PropTypes.number,
};

SecondaryPagination.defaultProps = {
  onPageChange: () => {},
  onLimitChange: () => {},
  limits: [10],
  pages: 1,
  defaultPage: 1,
  totalDocuments: 0,
  name: "secondaryPagination",
};

export default SecondaryPagination;
